import { useMemo } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { SrIcon } from "@/components/ui/SrIcon";
import { useCustomerActivities } from "@/hooks/use-customer-activities";
import { activityTypeLabel } from "@/lib/customer-activity-calc";
import { formatDate } from "@/lib/format";
import {
  resolveAiSources,
  setAiSourceChoice,
  type AiSourceChoice,
} from "@/lib/tilbudsarbeidsrom/ai-sources";

interface AiSourcesPanelProps {
  customerId: string | null;
  choices: AiSourceChoice[];
  readOnly: boolean;
  saving: boolean;
  onChange: (next: AiSourceChoice[]) => void;
}

/** Kilder fra kundekortet, med avkrysning for hva AI skal bruke (B6). */
export function AiSourcesPanel({ customerId, choices, readOnly, saving, onChange }: AiSourcesPanelProps) {
  const { data: activities = [], isLoading } = useCustomerActivities(customerId ?? undefined, { limit: 30 });
  const sources = useMemo(() => resolveAiSources(activities, choices), [activities, choices]);

  return (
    <aside className="flex flex-col gap-3" aria-labelledby="ai-sources-heading">
      <div className="flex items-baseline gap-2">
        <h2 id="ai-sources-heading" className="font-display text-base">Fra kundekortet</h2>
        <span className="text-xs text-muted-foreground ml-auto">Avkrysset brukes av AI</span>
      </div>

      {!customerId && (
        <p className="text-sm text-muted-foreground">Tilbudet er ikke koblet til en kunde i kunderegisteret.</p>
      )}
      {customerId && isLoading && (
        <SrIcon name="loader2" className="w-4 h-4 animate-spin text-muted-foreground" />
      )}
      {customerId && !isLoading && sources.length === 0 && (
        <p className="text-sm text-muted-foreground">
          Ingen notater, referater eller e-post på kundekortet ennå.
        </p>
      )}

      <ul className="flex flex-col gap-2">
        {sources.map(({ activity, included }) => {
          const id = `ai-source-${activity.id}`;
          return (
            <li key={activity.id} className="border border-border bg-card rounded-md p-3 flex gap-3">
              <Checkbox
                id={id}
                checked={included}
                disabled={readOnly || saving}
                onCheckedChange={(v) => onChange(setAiSourceChoice(choices, activity, v === true))}
                className="mt-0.5"
              />
              <label htmlFor={id} className="flex flex-col gap-1 min-w-0 cursor-pointer">
                <span className="text-[11px] uppercase tracking-wide font-medium text-muted-foreground">
                  {activityTypeLabel(activity.type)} · {formatDate(activity.occurred_at)}
                  {activity.user?.full_name ? ` · ${activity.user.full_name}` : ""}
                </span>
                {activity.subject && <span className="text-sm font-medium">{activity.subject}</span>}
                {activity.body && <span className="text-sm text-muted-foreground line-clamp-3">{activity.body}</span>}
              </label>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}
